import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast, Toaster } from "sonner";
import { Pencil, Trash2, RefreshCw, Database, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const formatDate = (d) => (d ? new Date(d).toLocaleString("hu-HU") : "—");

const SOURCE_LABELS = {
  api_football: "API-Football (RapidAPI/api-sports.io)",
  sofascore: "SofaScore (nem hivatalos)",
  demo: "Demo (offline mintaadat)",
};

const SOURCE_DESC = {
  api_football: "Hivatalos kulccsal, napi 100 lekérés. Megbízható odds.",
  sofascore: "Ingyenes scraping. Adatközpontról gyakran 403 hibát ad.",
  demo: "Beépített, mindig elérhető. Nem valós adat.",
};

function SettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (newSource) => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", { data_source: newSource });
      toast.success(`Adatforrás beállítva: ${SOURCE_LABELS[newSource]}`);
      setSettings({ ...settings, data_source: newSource });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const testSource = async (source) => {
    setTesting(source);
    try {
      const { data } = await api.post("/admin/settings/test", { data_source: source });
      setTestResults({ ...testResults, [source]: data });
      if (data.ok) toast.success(`${SOURCE_LABELS[source]}: ${data.matches} meccs`);
      else toast.error(`${SOURCE_LABELS[source]}: ${data.error}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(null);
    }
  };

  if (loading) return <div className="text-zinc-500 py-4">Beállítások betöltése...</div>;
  if (!settings) return null;

  return (
    <div className="border border-white/10 bg-[#0A0A0A] mb-8" data-testid="settings-panel">
      <div className="px-5 py-3 border-b border-white/10 bg-white/[0.02] flex items-center gap-2">
        <Database size={16} className="text-[#007AFF]" />
        <div className="font-display font-bold text-lg tracking-tight">Adatforrás Beállítások</div>
      </div>
      <div className="p-5 space-y-3">
        {settings.valid_sources.map((src) => {
          const active = settings.data_source === src;
          const test = testResults[src];
          return (
            <div
              key={src}
              data-testid={`source-row-${src}`}
              className={`grid grid-cols-12 gap-3 items-center px-4 py-3 border ${
                active ? "border-[#007AFF]/50 bg-[#007AFF]/5" : "border-white/10 bg-[#0A0A0A]"
              }`}
            >
              <div className="col-span-6">
                <div className="flex items-center gap-2">
                  <div className="font-medium">{SOURCE_LABELS[src]}</div>
                  {active && (
                    <Badge className="bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/30">
                      AKTÍV
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1">{SOURCE_DESC[src]}</div>
                {test && (
                  <div className="mt-2 text-xs flex items-center gap-2">
                    {test.ok ? (
                      <>
                        <CheckCircle2 size={12} className="text-[#22C55E]" />
                        <span className="text-[#22C55E] font-mono">{test.matches} meccs elérhető</span>
                      </>
                    ) : (
                      <>
                        <XCircle size={12} className="text-[#EF4444]" />
                        <span className="text-[#EF4444] font-mono">{test.error}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="col-span-6 flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => testSource(src)}
                  disabled={testing === src}
                  data-testid={`test-source-${src}`}
                  className="border-white/15 hover:bg-white/5 text-zinc-200"
                >
                  {testing === src ? <Loader2 size={14} className="animate-spin" /> : "Tesztelés"}
                </Button>
                <Button
                  size="sm"
                  onClick={() => save(src)}
                  disabled={active || saving}
                  data-testid={`activate-source-${src}`}
                  className={active
                    ? "bg-white/5 text-zinc-500 cursor-not-allowed"
                    : "bg-[#007AFF] hover:bg-[#3395FF] text-white"}
                >
                  {active ? "Aktív" : "Aktiválás"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-5 py-3 border-t border-white/10 bg-white/[0.02] text-xs text-zinc-500">
        Tipp: A "Tesztelés" gomb megpróbálja lekérdezni a forrást anélkül, hogy aktiválná. A beállítás
        után a következő frissítésnél már az új forrás adatait kapod.
      </div>
    </div>
  );
}

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ name: "", role: "user", disabled: false, password: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || "", role: u.role || "user", disabled: !!u.disabled, password: "" });
  };

  const saveEdit = async () => {
    try {
      const payload = { name: form.name, role: form.role, disabled: form.disabled };
      if (form.password) payload.password = form.password;
      await api.patch(`/admin/users/${editing.id}`, payload);
      toast.success("Felhasználó frissítve");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/users/${deleting.id}`);
      toast.success("Felhasználó törölve");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Layout>
      <Toaster position="top-right" theme="dark" />
      <div className="px-8 py-6">
        <div className="flex items-end justify-between mb-6 pb-5 border-b border-white/10">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1">Adminisztráció</div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Admin Panel</h1>
            <p className="text-sm text-zinc-400 mt-1">Felhasználók és adatforrás kezelése.</p>
          </div>
          <Button onClick={load} disabled={loading} variant="outline" data-testid="admin-refresh-btn"
            className="border-white/15 hover:bg-white/5 text-zinc-200">
            <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Frissítés
          </Button>
        </div>

        <SettingsPanel />

        <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-3">Felhasználók</div>
        <div className="border border-white/10">
          <div className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-white/10 bg-white/[0.02] text-[10px] tracking-wider text-zinc-500 uppercase">
            <div className="col-span-3">E-mail</div>
            <div className="col-span-2">Név</div>
            <div className="col-span-1">Szerep</div>
            <div className="col-span-1">Állapot</div>
            <div className="col-span-2">Létrehozva</div>
            <div className="col-span-2">Utolsó belépés</div>
            <div className="col-span-1 text-right">Műveletek</div>
          </div>
          {users.map((u) => (
            <div key={u.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/[0.02]"
              data-testid={`user-row-${u.email}`}>
              <div className="col-span-3 text-sm font-mono truncate">{u.email}</div>
              <div className="col-span-2 text-sm">{u.name || "—"}</div>
              <div className="col-span-1">
                <Badge className={u.role === "admin"
                  ? "bg-[#007AFF]/15 text-[#3395FF] border border-[#007AFF]/30"
                  : "bg-white/5 text-zinc-300 border border-white/10"}>
                  {u.role}
                </Badge>
              </div>
              <div className="col-span-1">
                {u.disabled ? (
                  <Badge className="bg-[#EF4444]/15 text-[#EF4444] border border-[#EF4444]/30">Letiltva</Badge>
                ) : (
                  <Badge className="bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/30">Aktív</Badge>
                )}
              </div>
              <div className="col-span-2 text-xs text-zinc-400 font-mono">{formatDate(u.created_at)}</div>
              <div className="col-span-2 text-xs text-zinc-400 font-mono">{formatDate(u.last_login)}</div>
              <div className="col-span-1 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}
                  className="h-7 w-7 p-0 text-zinc-400 hover:text-white hover:bg-white/5">
                  <Pencil size={14} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDeleting(u)} data-testid={`delete-user-${u.email}`}
                  className="h-7 w-7 p-0 text-zinc-400 hover:text-[#EF4444] hover:bg-[#EF4444]/10">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
          {users.length === 0 && (
            <div className="text-center py-12 text-zinc-500">Nincs felhasználó.</div>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">Felhasználó szerkesztése</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">{editing?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Név</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="edit-name-input" className="mt-1.5 bg-[#141414] border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Szerep</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5 bg-[#141414] border-white/10" data-testid="edit-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-white/10">
                  <SelectItem value="user">Felhasználó</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between p-3 border border-white/10">
              <div>
                <div className="text-sm font-medium">Letiltva</div>
                <div className="text-xs text-zinc-500">A letiltott felhasználók nem tudnak belépni.</div>
              </div>
              <Switch checked={form.disabled} onCheckedChange={(v) => setForm({ ...form, disabled: v })}
                data-testid="edit-disabled-switch" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Új jelszó (opcionális)</Label>
              <Input type="password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="edit-password-input" className="mt-1.5 bg-[#141414] border-white/10"
                placeholder="Üresen hagyva nem változik" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-white/15"
              data-testid="cancel-edit-btn">Mégse</Button>
            <Button onClick={saveEdit} className="bg-[#007AFF] hover:bg-[#3395FF]" data-testid="save-edit-btn">
              Mentés
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">Felhasználó törlése</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Biztosan törölni szeretnéd a(z) <span className="text-white font-mono">{deleting?.email}</span> fiókot?
              Ez a művelet nem visszavonható.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5">Mégse</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[#EF4444] hover:bg-[#DC2626]"
              data-testid="confirm-delete-btn">
              Törlés
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
